/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  AdminUserAttributes,
  AuthType,
  CustomerUserAttributes,
  MerchantUserAttributes,
} from './auth.types';

export interface FullScreen {
  mustBeFullScreen: boolean;
  isFullScreen: boolean;
}

/**
 * Represents an item in the inventory breakdown (parent or child fraction)
 */
export interface BreakdownItem {
  name: string;
  fraction: number;
  units: number;
  id: string;
}

export enum WebWorkerActions {
  SYNC_PRODUCTS = 'sync-products',
  SYNC_TAXES = 'sync-taxes',
  SYNC_DISCOUNTS = 'sync-discounts',
  SYNC_CUSTOMERS = 'sync-customers',
  SYNC_OFFLINE_RECEIPTS = 'sync-offline-receipts',
  SYNC_ONLINE_RECEIPTS = 'sync-online-receipts',
  SYNC_PAYMENT_TYPES = 'sync-payment-types',
  SYNC_LOYALTY_PROGRAMS = 'sync-loyalty-programs',
  SYNC_OFFLINE_CUSTOMERS = 'sync-offline-customers',
  BULK_CREATE_PRODUCTS = 'bulk-create-products',
  BULK_IMPORT_PRODUCTS = 'bulk-import-products',
  BULK_UPDATE_PRODUCTS = 'bulk-update-products',
  START_POLLING = 'start-polling',
  STOP_POLLING = 'stop-polling',
}

export interface InventoryLevel {
  variant_id: string;
  store_id: string;
  in_stock: number;
  updated_at: string;
  update_id?: string; // Does not come with the webhook
}

export interface InventoryLevelsOutput {
  inventory_levels: InventoryLevel[];
}

export interface InventoryLevelProps {
  variant_id: string;
  store_id: string;
  stock_after: number;
}

export interface InventoryLevelsProps {
  inventory_levels: InventoryLevelProps[];
}

export interface ShopAndSmileImageProps {
  image_url: string;
  item_id: string;
}

export interface ShopAndSmileOkResponse {
  OK: boolean;
  message?: string;
}

export interface Currency {
  code: string;
  decimal_places: number;
  symbol?: string | null | undefined;
}

export interface BackupDto {
  sku: string;
  name: string;
  price: number;
  inventory: number;
  image_s3_key: string;
  old_image_url: string;
  merchant_id: string;
  trigger_image_backup: boolean;
  new_image_url?: string;
}

export interface BackupItem {
  PK: string;
  SK: string;
  GSI1PK: string;
  GSI1SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
  EntityType: EntityTypes;
  sku: string;
  name: string;
  price: number;
  inventory: number;
  image_s3_key: string;
  merchant_id: string;
  old_image_url: string;
  trigger_image_backup: boolean;
  new_image_url?: string;
}

export interface MerchantInfo {
  id: string;
  business_name: string;
  email: string;
  country: string;
  currency: Currency;
  created_at: string;
}

export interface WriteResponse {
  message: string;
  isSuccessful: boolean;
}

export enum GSI_DESCRIPTORS {
  CONFIRMATION_TYPE = 'CONFIRMATION_TYPE',
  EVENT_TYPE = 'EVENT_TYPE',
  REASON = 'REASON',
  SOURCE_TYPE = 'SOURCE_TYPE',
  DATE = 'DATE',
  /** When a record was applied (processed), as opposed to when it happened. */
  PROCESSED = 'PROCESSED',
  FAILURE = 'FAILURE',
  SUCCESS = 'SUCCESS',
  CORRELATION = 'CORRELATION',
  DOMAIN = 'DOMAIN',
  PURCHASE_ORDER_COUNT = 'PURCHASE_ORDER_COUNT',
  PAYMENT_STATUS = 'PAYMENT_STATUS',
  PURCHASE_STATUS = 'PURCHASE_STATUS',
  LOAN_COUNT = 'LOAN_COUNT',
  PHONE_NUMBER = 'PHONE_NUMBER',
  START_DATE = 'START_DATE',
  END_DATE = 'END_DATE',
  YEAR_MONTH = 'YYMM',
  STATUS = 'STATUS',
  IS_MOTHER = 'IS_MOTHER',
  MAC_ADDRESS = 'MAC_ADDRESS',
  MOTHER = 'MOTHER',
  CHILD = 'CHILD',
  ENTITY_TYPE = 'ENTITY_TYPE',
  PURCHASE_ORDER_PAYMENT_SUMMARY_VERSION = 'PURCHASE_ORDER_PAYMENT_SUMMARY_VERSION',
  VERSION = 'VERSION',
  STORE_SALES_REPORT_VERSION = 'STORE_SALES_REPORT_VERSION',
  EMPLOYEE_SALES_REPORT_VERSION = 'EMPLOYEE_SALES_REPORT_VERSION',
  PRODUCT_SALES_REPORT_VERSION = 'PRODUCT_SALES_REPORT_VERSION',
  STORE_SALES_REPORT_SNAPSHOT_VERSION = 'STORE_SALES_REPORT_SNAPSHOT_VERSION',
  EMPLOYEE_SALES_REPORT_SNAPSHOT_VERSION = 'EMPLOYEE_SALES_REPORT_SNAPSHOT_VERSION',
  PRODUCT_SALES_REPORT_SNAPSHOT_VERSION = 'PRODUCT_SALES_REPORT_SNAPSHOT_VERSION',
  PRODUCT_INVENTORY_VALUE_VERSION = 'PRODUCT_INVENTORY_VALUE_VERSION',
  TOTAL_DUE = 'TOTAL_DUE',
  DUE_DATE = 'DUE_DATE',
  POS_RECEIPT = 'POS_RECEIPT',
  COGNITO_USER = 'COGNITO_USER',
  COGNITO_USERNAME = 'COGNITO_USERNAME',
  IS_PARENT = 'IS_PARENT',
  GLOBAL_SKU = 'GLOBAL_SKU',
  INVENTORY_TYPE = 'INVENTORY_TYPE',
  DEBT_STATUS = 'DEBT_STATUS',
  DEBT_ORIGIN = 'DEBT_ORIGIN',
  DEBT_VERSION = 'DEBT_VERSION',
  EntityIndexPK = 'EntityIndexPK',
  EntityIndexSK = 'EntityIndexSK',
  FLOW_TYPE = 'FLOW_TYPE',
  TOPUP_AMOUNT = 'TOPUP_AMOUNT',
  TOPUP_RECEIVER = 'TOPUP_RECEIVER',
  TOPUP_SENDER = 'TOPUP_SENDER',
  PRICING_PLAN_STATUS = 'PRICING_PLAN_STATUS',
  AGREEMENT_STATUS = 'AGREEMENT_STATUS',
  MERCHANT = 'MERCHANT',
  ACTIVE_CUSTOMER_AGREEMENT = 'ACTIVE_CUSTOMER_AGREEMENT',
  PLAN = 'PLAN',
  AGREEMENT = 'AGREEMENT',
  SHIFT_STATUS = 'SHIFT_STATUS',
  FLOW = 'FLOW',
  ORIGIN = 'ORIGIN',
  CREATOR = 'CREATOR',
  APPROVAL_STATUS = 'APPROVAL_STATUS',
  PAYMENT_METHOD = 'PAYMENT_METHOD',
  TRANSFERER = 'TRANSFERER',
  TRANSFEREE = 'TRANSFEREE',
  PK = 'PK',
  SK = 'SK',
  GSI1PK = 'GSI1PK',
  GSI1SK = 'GSI1SK',
  GSI2PK = 'GSI2PK',
  GSI2SK = 'GSI2SK',
  GSI3PK = 'GSI3PK',
  GSI3SK = 'GSI3SK',
  GSI4PK = 'GSI4PK',
  GSI4SK = 'GSI4SK',
  GSI5PK = 'GSI5PK',
  GSI5SK = 'GSI5SK',
  GSI6PK = 'GSI6PK',
  GSI6SK = 'GSI6SK',
  GSI7PK = 'GSI7PK',
  GSI7SK = 'GSI7SK',
  GSI8PK = 'GSI8PK',
  GSI8SK = 'GSI8SK',
  GSI9PK = 'GSI9PK',
  GSI9SK = 'GSI9SK',
  RECIPIENT = 'RECIPIENT',
  NOTIFICATION_TYPE = 'NOTIFICATION_TYPE',
  MESSAGE_ID = 'MESSAGE_ID',
  DAY = 'DAY',
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum EntityTypes {
  STORE_WALLET = 'STORE_WALLET',
  MERCHANT_WALLET = 'MERCHANT_WALLET',
  MERCHANT_PAYMENT = 'MERCHANT_PAYMENT',
  TRANSFEREE = 'TRANSFEREE',
  TRANSFERER = 'TRANSFERER',
  ACCOUNT = 'ACCOUNT',
  BACKUP = 'BACKUP',
  WEBSOCKET_CONNECTION = 'WEBSOCKET_CONNECTION',
  BORROWER_LOAN = 'BORROWER_LOAN',
  CURRENT_INVENTORY = 'CURRENT_INVENTORY',
  CUSTOMER = 'CUSTOMER',
  CUSTOMER_DEBT = 'CUSTOMER_DEBT',
  CUSTOMER_LIABILITIES = 'CUSTOMER_LIABILITIES',
  DEBT = 'DEBT',
  DEBT_CHANGE = 'DEBT_CHANGE',
  DEBT_PAYMENT = 'DEBT_PAYMENT',
  DISCOUNT = 'DISCOUNT',
  EMPLOYEE_SALES_REPORT = 'EMPLOYEE_SALES_REPORT',
  EMPLOYEE_SALES_REPORT_SNAPSHOT = 'EMPLOYEE_SALES_REPORT_SNAPSHOT',
  ERROR = 'ERROR',
  EVENT = 'EVENT',
  IMAGE = 'IMAGE',
  INVENTORY_CHANGE = 'INVENTORY_CHANGE',
  INVENTORY_DISCREPANCY = 'INVENTORY_DISCREPANCY',
  INVENTORY_HISTORY = 'INVENTORY_HISTORY',
  INVENTORY_TEMPLATE = 'INVENTORY_TEMPLATE',
  ITEM_INVENTORY_VALUE = 'ITEM_INVENTORY_VALUE',
  DAILY_COUNT = 'DAILY_COUNT',
  LOAN = 'LOAN',
  LOYALTY_PROGRAM = 'LOYALTY_PROGRAM',
  /** Marks a receipt's loyalty as applied to a customer (dedupe, expires). */
  LOYALTY_RECEIPT = 'LOYALTY_RECEIPT',
  /** Marks an event's debt payment as recorded (dedupe, expires). */
  DEBT_PAYMENT_DEDUPE = 'DEBT_PAYMENT_DEDUPE',
  /** Marks a stock change (e.g. a receipt line) as applied (dedupe, expires). */
  INVENTORY_CHANGE_DEDUPE = 'INVENTORY_CHANGE_DEDUPE',
  /** Marks a purchase-order create as done, per idempotency key (kept). */
  PURCHASE_ORDER_DEDUPE = 'PURCHASE_ORDER_DEDUPE',
  /** A stock operation's record and replay marker (service-inventory). */
  STOCK_OPERATION = 'STOCK_OPERATION',
  MERCHANT = 'MERCHANT',
  PAYMENT = 'PAYMENT',
  PAYMENT_TYPE = 'PAYMENT_TYPE',
  PLATFORM_PAYMENT_TYPE = 'PLATFORM_PAYMENT_TYPE',
  POS_DEVICE = 'POS_DEVICE',
  PRODUCT = 'PRODUCT',
  PRODUCT_CHANGE = 'PRODUCT_CHANGE',
  PRODUCT_FRACTION = 'PRODUCT_FRACTION',
  PRODUCT_FRACTION_CHANGE = 'PRODUCT_FRACTION_CHANGE',
  PRODUCT_SALES_REPORT = 'PRODUCT_SALES_REPORT',
  PRODUCT_SALES_REPORT_SNAPSHOT = 'PRODUCT_SALES_REPORT_SNAPSHOT',
  PRODUCT_VERSION = 'PRODUCT_VERSION',
  PROMOTION = 'PROMOTION',
  PURCHASE_ORDER = 'PURCHASE_ORDER',
  PURCHASE_ORDER_ITEM = 'PURCHASE_ORDER_ITEM',
  PURCHASE_ORDER_PAYMENT = 'PURCHASE_ORDER_PAYMENT',
  PURCHASE_ORDER_PAYMENT_HISTORY = 'PURCHASE_ORDER_PAYMENT_HISTORY',
  PURCHASE_ORDER_PAYMENT_SUMMARY = 'PURCHASE_ORDER_PAYMENT_SUMMARY',
  RECEIPT = 'RECEIPT',
  RECEIPT_HISTORY = 'RECEIPT_HISTORY',
  STORE = 'STORE',
  STORE_PRODUCT = 'STORE_PRODUCT',
  STORE_PRODUCT_CHANGE = 'STORE_PRODUCT_CHANGE',
  STORE_PRODUCT_VERSION = 'STORE_PRODUCT_VERSION',
  STORE_SALES_REPORT = 'STORE_SALES_REPORT',
  STORE_SALES_REPORT_SNAPSHOT = 'STORE_SALES_REPORT_SNAPSHOT',
  STORE_SCALE_CONFIG = 'STORE_SCALE_CONFIG',
  STORE_SCALE_SHORTCUT = 'STORE_SCALE_SHORTCUT',
  STORE_FAVOURITE_CELL = 'STORE_FAVOURITE_CELL',
  STORE_VALUE_SNAPSHOT = 'STORE_VALUE_SNAPSHOT',
  SUPPLIER = 'SUPPLIER',
  SYSTEM = 'SYSTEM',
  TAX = 'TAX',
  THRESHOLD = 'THRESHOLD',
  USER = 'USER',
  PERMISSION = 'PERMISSION',
  PRINCIPAL = 'PRINCIPAL',
  ROLE = 'ROLE',
  SALE = 'SALE',
  CREATE_RECEIPT = 'CREATE_RECEIPT',
  INVOICE = 'INVOICE',
  PRICING_PLAN = 'PRICING_PLAN',
  CUSTOMER_AGREEMENT = 'CUSTOMER_AGREEMENT',
  WALLET_TRANSFER_OUT = 'WALLET_TRANSFER_OUT',
  PENDING_TOPUP = 'PENDING_TOPUP',
  SHIFT = 'SHIFT',
  SHIFT_TRANSACTION = 'SHIFT_TRANSACTION',
  TOPUP_REQUEST = 'TOPUP_REQUEST',
  WALLET_MESSAGE = 'WALLET_MESSAGE',
  TOPUP_PASSWORD = 'TOPUP_PASSWORD',
  ACCOUNT_SNAPSHOT = 'ACCOUNT_SNAPSHOT',
  ACCOUNT_TRANSACTION = 'ACCOUNT_TRANSACTION',
  ACCOUNT_TRANSFER_REQUEST = 'ACCOUNT_TRANSFER_REQUEST',
  TOPUP_NUMBER = 'TOPUP_NUMBER',
  TOPUP_DEVICE = 'TOPUP_DEVICE',
  INVENTORY_TRANSFER = 'INVENTORY_TRANSFER',
  INVENTORY_TRANSFER_ITEM = 'INVENTORY_TRANSFER_ITEM',
  GLOBAL_PRODUCT = 'GLOBAL_PRODUCT',
  GLOBAL_PRODUCT_FRACTION = 'GLOBAL_PRODUCT_FRACTION',
  GLOBAL_PRODUCT_BARCODE = 'GLOBAL_PRODUCT_BARCODE',
  GLOBAL_PRODUCT_TAG = 'GLOBAL_PRODUCT_TAG',
  SALES_AGENT = 'SALES_AGENT',
  PO_PAYMENT_STORE_SNAPSHOT = 'PO_PAYMENT_STORE_SNAPSHOT',
  PO_PAYMENT_SUPPLIER_SNAPSHOT = 'PO_PAYMENT_SUPPLIER_SNAPSHOT',
  PO_RECEIVE_EVENT = 'PO_RECEIVE_EVENT',
  PRODUCT_EXPIRY = 'PRODUCT_EXPIRY',
  POS_SESSION_EVENT = 'POS_SESSION_EVENT',
  WEIGHTED_SALE = 'WEIGHTED_SALE',
  MERCHANT_WALLET_TRANSACTION = 'MERCHANT_WALLET_TRANSACTION',
  MERCHANT_WALLET_SNAPSHOT = 'MERCHANT_WALLET_SNAPSHOT',
  STORE_WALLET_TRANSACTION = 'STORE_WALLET_TRANSACTION',
  STORE_WALLET_SNAPSHOT = 'STORE_WALLET_SNAPSHOT',
  BILLING_RUN = 'BILLING_RUN',
  CUSTOMER_AGREEMENT_AUDIT = 'CUSTOMER_AGREEMENT_AUDIT',
  WHATSAPP_WEBHOOK_EVENT = 'WHATSAPP_WEBHOOK_EVENT',
  IFTTT_SMS_MESSAGE = 'IFTTT_SMS_MESSAGE',
  // Integrations with other POS systems (service-integrations).
  INTEGRATION = 'INTEGRATION',
  INTEGRATION_CONNECTION = 'INTEGRATION_CONNECTION',
  INTEGRATION_MAPPINGS = 'INTEGRATION_MAPPINGS',
  INTEGRATION_PRODUCT_LINK = 'INTEGRATION_PRODUCT_LINK',
  INTEGRATION_IMPORT_JOB = 'INTEGRATION_IMPORT_JOB',
  FAILED_EVENT = 'FAILED_EVENT',
  NOTIFICATION = 'NOTIFICATION',
  NOTIFICATION_RECIPIENT = 'NOTIFICATION_RECIPIENT',
  NOTIFICATION_SUBSCRIPTION = 'NOTIFICATION_SUBSCRIPTION',
  NOTIFICATION_SCHEDULE = 'NOTIFICATION_SCHEDULE',
  NOTIFICATION_MESSAGE_ID_COUNTER = 'NOTIFICATION_MESSAGE_ID_COUNTER',
  NOTIFICATION_DEDUPE = 'NOTIFICATION_DEDUPE',
  NOTIFICATION_CONTACT = 'NOTIFICATION_CONTACT',
  NOTIFICATION_CONTACT_VERIFICATION = 'NOTIFICATION_CONTACT_VERIFICATION',
  NOTIFICATION_USAGE = 'NOTIFICATION_USAGE',
  FX_RATE = 'FX_RATE',
  ADMIN_LOGIN_CHALLENGE = 'ADMIN_LOGIN_CHALLENGE',
  ADMIN_SESSION = 'ADMIN_SESSION',
  PLATFORM_FOUNDER = 'PLATFORM_FOUNDER',
  PLATFORM_EXPENSE = 'PLATFORM_EXPENSE',
  PLATFORM_INCOME = 'PLATFORM_INCOME',
  PERMISSION_VISIBILITY = 'PERMISSION_VISIBILITY',
  WHATSAPP_SETTINGS = 'WHATSAPP_SETTINGS',
  ANNOUNCEMENT = 'ANNOUNCEMENT',
  ATTACHMENT = 'ATTACHMENT',
  TASK = 'TASK',
  STOCK_ORDER = 'STOCK_ORDER',
  STOCK_TRANSFER = 'STOCK_TRANSFER',
}

export enum RelationshipTypes {
  MOTHER = 'MOTHER',
  CHILD = 'CHILD',
  INVENTORY_VARIANT = 'INVENTORY_VARIANT',
}

export enum DefaultPricingType {
  FIXED = 'FIXED',
  VARIABLE = 'VARIABLE',
}

export interface Paginated {
  cursor?: string | null | undefined;
  count: number;
}

export interface EmployeePage {
  employees: Employee[];
  cursor?: string;
  count: number;
}

export enum Form {
  SQUARE = 'SQUARE',
  CIRCLE = 'CIRCLE',
  SUN = 'SUN',
  OCTAGON = 'OCTAGON',
}

export enum Color {
  GREY = 'GREY',
  RED = 'RED',
  PINK = 'PINK',
  ORANGE = 'ORANGE',
  YELLOW = 'YELLOW',
  GREEN = 'GREEN',
  BLUE = 'BLUE',
  PURPLE = 'PURPLE',
}

export interface Component {
  variant_id: string;
  quantity: number;
}

export type UpdateAvailabilityPropsStore = {
  store_id: string;
  available_for_sale: boolean;
};

export type UpdateAvailabilityPropsVariant = {
  variant_id: string;
  item_id: string;
  stores: UpdateAvailabilityPropsStore[];
};

/**
 * Written by the versioned-write helper on every change (AGENTS.md §4.4.1).
 * Optional for backwards compatibility: rows written before an entity
 * migrated have no version (missing = 0) and no recorded actor.
 */
export interface Versioned {
  /** Incremented on every write; the history table keeps one row per version. */
  version?: number | null;
  /** Id of the user who made the latest change (`system:<service>` for jobs). */
  updated_by_id?: string | null;
  /** Name of the user who made the latest change. */
  updated_by_name?: string | null;
}

export interface Timestamps {
  /**
   * The ISO 8601 formatted date-time string indicating when this payment type was created.
   * @example "2020-03-25T19:55:23.077Z"
   */
  created_at: string;

  /**
   * The ISO 8601 formatted date-time string indicating when this payment type was last updated.
   * @example "2020-03-30T08:05:10.020Z"
   */
  updated_at: string;

  /**
   * The ISO 8601 formatted date-time string indicating when this payment type was deleted.
   * This field is optional and will be present only if the payment type has been deleted.
   * @example "2020-04-02T23:45:20.050Z"
   */
  deleted_at?: string | null;

  /**
   * Audit trail: id of the user who soft-deleted this record. Optional for
   * backwards compatibility with legacy rows that only have `deleted_at`.
   */
  deleted_by_id?: string | null;

  /**
   * Audit trail: display name of the user who soft-deleted this record.
   */
  deleted_by_name?: string | null;

  /**
   * The ISO 8601 formatted date-time string indicating when this record
   * was restored from a soft-delete. Mutually exclusive with `deleted_at`
   * by convention: writers clear the delete triplet when setting restore
   * fields (and vice versa). If both happen to exist, the read layer
   * keeps only whichever event happened most recently.
   */
  restored_at?: string | null;

  /**
   * Audit trail: id of the user who restored this record. Optional for
   * backwards compatibility with legacy rows that pre-date the restore
   * audit feature.
   */
  restored_by_id?: string | null;

  /**
   * Audit trail: display name of the user who restored this record.
   */
  restored_by_name?: string | null;
}

export type AuthUser =
  | CustomerUserAttributes
  | MerchantUserAttributes
  | AdminUserAttributes;

export interface Employee extends Timestamps, Versioned {
  id: string;
  name: string;
  roles: string[];
  username: string;
  email: string;
  phone_number: string;
  stores: string[];
  merchant_id: string;
  is_active: boolean;
  is_inactive_since?: string;
  cognito_id: string | null | undefined;
  cognito_username: string | null | undefined;
  is_owner: boolean;
  auth_user?: MerchantUserAttributes;
  /** Set to true once the user's phone number has been verified via OTP. */
  is_number_verified?: boolean;
  /**
   * Interface language the user picked (web or POS), applied in every app.
   * Optional for backwards compatibility; missing = the merchant default.
   */
  language?: UiLanguage | null;
}

export interface EmployeeSummary {
  id: string;
  name: string;
}

export enum ErrorCodes {
  NOT_FOUND = 'NOT_FOUND',
  FORBIDDEN = 'FORBIDDEN',
  UNAUTHORIZED = 'UNAUTHORIZED',
  BAD_REQUEST = 'BAD_REQUEST',
  INVALID_PARAMETER = 'INVALID_PARAMETER',
  INVALID_REQUEST = 'INVALID_REQUEST',
  INVALID_COMMAND = 'INVALID_COMMAND',
  CONFLICTING_REQUEST = 'CONFLICTING_REQUEST',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  NO_RESPONSE = 'NO_RESPONSE',
}

export interface DateRange {
  to: string;
  from: string;
}

export enum ReceiptType {
  SALE = 'SALE',
  REFUND = 'REFUND',
}

export interface TotalMoneyRange {
  to: number;
  from: number;
}

export interface EventBusDto {
  data: any;
  metadata: Record<string, unknown>;
}

export enum ShopAndSmileEventSource {
  SHOP_AND_SMILE = 'shop-and-smile',
  SHOP_AND_SMILE_PRODUCTS = 'shop-and-smile.products',
  SHOP_AND_SMILE_INVENTORY = 'shop-and-smile.inventory',
  SHOP_AND_SMILE_RECEIPTS = 'shop-and-smile.receipts',
  SHOP_AND_SMILE_REPORTS = 'shop-and-smile.reports',
  SHOP_AND_SMILE_USERS = 'shop-and-smile.users',
  SHOP_AND_SMILE_CUSTOMERS = 'shop-and-smile.customers',
  SHOP_AND_SMILE_PAYMENTS = 'shop-and-smile.payments',
  SHOP_AND_SMILE_LOYALTY = 'shop-and-smile.loyalty',
  SHOP_AND_SMILE_DEBTS = 'shop-and-smile.debts',
  SHOP_AND_SMILE_PURCHASE_ORDERS = 'shop-and-smile.purchase-orders',
  SHOP_AND_SMILE_SUPPLIERS = 'shop-and-smile.suppliers',
  SHOP_AND_SMILE_STORES = 'shop-and-smile.stores',
  SHOP_AND_SMILE_SHIFTS = 'shop-and-smile.shifts',
  SHOP_AND_SMILE_NOTIFICATIONS = 'shop-and-smile.notifications',
}

export interface PaginationParams {
  cursor?: string | null | undefined;
  limit?: number;
}

export interface UploadImageToItemProps {
  item_id: string;
  image_url: string;
}

export interface Connection {
  entityId: string;
  entityType: EntityTypes;
  connectionId: string;
  connected: boolean;
  stage: string;
  domainName: string;
}

export interface ConnectionItem extends Connection {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
  GSI1PK: string;
  GSI1SK: string;
}

export interface SSMParameterConfig {
  path: string;
  outputKey: string;
  description?: string;
  secure?: boolean;
}

// Define the user roles as an enum
export enum UserRole {
  MANAGER = 'MANAGER',
  ADMIN = 'ADMIN',
  WAITER = 'WAITER',
  SYSTEM = 'SYSTEM',
  CASHIER = 'CASHIER',
  RECEIPT_CHECKER = 'RECEIPT_CHECKER',
  INVENTORY_COUNTER = 'INVENTORY_COUNTER',
  SUPPLIER = 'SUPPLIER',
}

export enum PaymentName {
  CASH = 'Cash',
  DEBT = 'Debt',
  BANK_CARD = 'Bank Card',
  MOBILE_MONEY = 'Mobile Money',
  SPLIT = 'Split',
  OTHER = 'Other',
  EMOLA = 'Emola',
  MPEZA = 'Mpeza',
  MTN = 'MTN Mobile Money',
  AIRTEL = 'Airtel Money',
  TIGO = 'Tigo Pesa',
  ZAMTEL = 'Zamtel',
}

// Define the user interface
export interface User {
  name: string;
  surname: string;
  phonenumber: string;
  code: string;
  user_id: string;
  role: UserRole;
  auth_type?: AuthType;
  /** Set to true once the user's phone number has been verified via OTP. */
  is_number_verified?: boolean;
}

export interface CreateUserProps {
  name: string;
  surname: string;
  phonenumber?: string;
  code: string;
  role: UserRole;
  merchant_id: string;
  stores: string[];
}

export interface ReceiptQueryParams extends PaginationParams {
  receipt_numbers?: string;
  since_receipt_number?: string;
  before_receipt_number?: string;
  store_id?: string;
  order?: string;
  updated_at_min?: string;
  updated_at_max?: string;
  created_at_min?: string;
  created_at_max?: string;
}

export interface CommonEmployeeAsCreatorAttributes {
  creator_id: string;
  creator_type: EntityTypes;
  creator_name: string;
}

export interface UserStore {
  merchant_id: string;
  store_id: string;
  user_id: string;
}

export interface CognitoStoresAttribute extends Timestamps {
  stores: UserStore[];
}

export enum StringBoolean {
  TRUE = 'true',
  FALSE = 'false',
}

/**
 * Where the location coordinates persisted on a session/event came from.
 * - 'gps': captured by the client device via OS geolocation.
 * - 'ip': resolved server-side from the request source IP.
 * - 'unknown': neither path produced a coordinate.
 */
export type LocationSource = 'gps' | 'ip' | 'unknown';

export interface ChangedAttributes {
  [key: string]: {
    old: any;
    new: any;
  };
}

export enum SoftDeleteFlag {
  INCLUDE_DELETED = 'INCLUDE_DELETED',
  EXCLUDE_DELETED = 'EXCLUDE_DELETED',
}

export type LambdaFunctionLoggingStatus = {
  name: string;
  arn: string;
  loggingAllowed: boolean;
  /**
   * Source describing how loggingAllowed was derived.
   * - explicit_env: IS_LOGGING_ALLOWED env var was set on the function.
   * - stage_default: derived from stage defaults when env var absent.
   * - unknown: could not infer a source.
   */
  loggingSource: 'explicit_env' | 'stage_default' | 'unknown';
  rawLoggingValue: string | null;
  stage?: string;
};

export type LambdaFunctionsListResponse = {
  stage?: string;
  total: number;
  retrievedAt: string;
  functions: LambdaFunctionLoggingStatus[];
};

export type BulkLambdaLoggingRequest = {
  functions: string[];
  allow: boolean;
};

export type BulkLambdaLoggingResponse = {
  allow: boolean;
  requestedCount: number;
  updatedCount: number;
  failedCount: number;
  updated: LambdaFunctionLoggingStatus[];
  failed: { identifier: string; error: string }[];
  status: 'completed' | 'completed_with_errors';
};

export type ToggleLoggingResponse = {
  allow: boolean;
  updatedCount: number;
  failedCount: number;
  updatedFunctions: string[];
  failedFunctions: { name: string; error: string }[];
  status: 'completed' | 'completed_with_errors';
};

/**
 * The Shop and Smile Lite app script URL held in an environment-specific
 * SSM parameter. `url` is null when the parameter has not been created
 * for the stage yet.
 */
export type LiteAppUrlSettings = {
  stage: string;
  url: string | null;
  version: number | null;
  lastModifiedAt: string | null;
};

export type UpdateLiteAppUrlRequest = {
  url: string;
};

export interface DynamoDBItem {
  PK: string;
  SK: string;
  EntityIndexPK: string;
  EntityIndexSK: string;
  GSI1PK?: string;
  GSI1SK?: string;
  GSI2PK?: string;
  GSI2SK?: string;
  GSI3PK?: string;
  GSI3SK?: string;
  GSI4PK?: string;
  GSI4SK?: string;
  GSI5PK?: string;
  GSI5SK?: string;
  GSI6PK?: string;
  GSI6SK?: string;
  GSI7PK?: string;
  GSI7SK?: string;
}

export interface AppBuild {
  version: string;
  releaseDate: string;
  platform: string;
  downloadUrl: string;
  archiveUrl: string;
  size: string;
  description: string;
  prerelease: boolean;
}

export interface AppBuildsResponse {
  android: AppBuild[];
  windows: AppBuild[];
  lite?: {
    android: AppBuild[];
    windows: AppBuild[];
  };
  // The Back Office wrapper ships for Android only
  backoffice?: {
    android: AppBuild[];
  };
}

export interface AndroidVersionInfo {
  apkSize?: string;
  zipSize?: string;
  lastModified: Date;
}

export interface WindowsVersionInfo {
  exeSize?: string;
  zipSize?: string;
  lastModified: Date;
}

export interface ReleaseVersion {
  version: string;
  releaseDate: string;
  platform: string;
}

export interface ReleasesVersionsResponse {
  android: ReleaseVersion[];
  windows: ReleaseVersion[];
}

export enum Language {
  EN_US = 'en_US',
  PT_PT = 'pt_PT',
}

/**
 * Interface language a user can pick (BCP 47). The web apps support all of
 * them; the POS supports `en` and `pt-PT` and uses the merchant default for
 * the others.
 */
export enum UiLanguage {
  EN = 'en',
  FR = 'fr',
  PT_PT = 'pt-PT',
}
